import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context';
import { userService } from '../../services';
import { Button, Card, LoadingSpinner, Input, Badge, Modal, useToastContext } from '../common';
import './UserDashboard.css';

const UserDashboard = () => {
  const { isAuthenticated, logout, user } = useAuth();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [isChangingPassword, setIsChangingPassword] = useState(false);

  const fetchProfile = useCallback(async () => {
    if (!isAuthenticated) {
      setLoading(false);
      return;
    }

    try {
      setIsRefreshing(true);
      const response = await userService.getProfile();
      if (response.success) {
        setProfile(response.data);
        setError(null);
      } else {
        setError(response.message || '프로필을 불러올 수 없습니다.');
      }
    } catch (err) {
      console.error('Profile fetch error:', err);
      setError('프로필을 불러오는 중 오류가 발생했습니다.');
    } finally {
      setLoading(false);
      setIsRefreshing(false);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    fetchProfile();
  }, [fetchProfile]);

  const { showToast } = useToastContext();

  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswordForm(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const validatePasswordForm = () => {
    const { currentPassword, newPassword, confirmPassword } = passwordForm;

    if (!currentPassword || !newPassword || !confirmPassword) {
      return '모든 필드를 입력해주세요.';
    }

    if (newPassword.length < 8) {
      return '새 비밀번호는 최소 8자 이상이어야 합니다.';
    }

    if (newPassword !== confirmPassword) {
      return '새 비밀번호와 확인 비밀번호가 일치하지 않습니다.';
    }

    if (currentPassword === newPassword) {
      return '새 비밀번호는 현재 비밀번호와 달라야 합니다.';
    }

    return null;
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();

    const validationError = validatePasswordForm();
    if (validationError) {
      showToast({
        type: 'error',
        title: '입력 오류',
        message: validationError,
        duration: 3000
      });
      return;
    }

    setIsChangingPassword(true);

    try {
      // 비밀번호 해시화
      const { hashPasswordWithSalt } = await import('../../utils/crypto');
      const hashedCurrentPassword = hashPasswordWithSalt(passwordForm.currentPassword, profile?.username || user?.username);
      const hashedNewPassword = hashPasswordWithSalt(passwordForm.newPassword, profile?.username || user?.username);
      
      const passwordData = {
        currentPassword: hashedCurrentPassword,
        newPassword: hashedNewPassword,
        confirmPassword: hashedNewPassword
      };
      
      const response = await userService.changePassword(passwordData);

      if (response.success) {
        showToast({
          type: 'success',
          title: '비밀번호 변경 성공',
          message: '비밀번호가 성공적으로 변경되었습니다.',
          duration: 3000
        });
        setPasswordForm({
          currentPassword: '',
          newPassword: '',
          confirmPassword: ''
        });
        setShowPasswordModal(false);
      } else {
        showToast({
          type: 'error',
          title: '비밀번호 변경 실패',
          message: response.error?.message || '비밀번호 변경에 실패했습니다.',
          duration: 4000
        });
      }
    } catch (error) {
      console.error('Password change error:', error);
      let errorMessage = '비밀번호 변경 중 오류가 발생했습니다.';
      if (error.response?.status === 401) {
        errorMessage = '현재 비밀번호가 올바르지 않습니다.';
      } else if (error.response?.data?.error?.message) {
        errorMessage = error.response.data.error.message;
      }
      showToast({
        type: 'error',
        title: '비밀번호 변경 오류',
        message: errorMessage,
        duration: 4000
      });
    } finally {
      setIsChangingPassword(false);
    }
  };

  const handleClosePasswordModal = () => {
    setShowPasswordModal(false);
    setPasswordForm({
      currentPassword: '',
      newPassword: '',
      confirmPassword: ''
    });
  };



  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now - date);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays === 1) {
      return '오늘';
    } else if (diffDays === 2) {
      return '어제';
    } else if (diffDays <= 7) {
      return `${diffDays - 1}일 전`;
    } else {
      return date.toLocaleDateString('ko-KR', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    }
  };

  if (!isAuthenticated) {
    return (
      <div className="page-background">
        <Card padding="lg">
          <Card.Content className="text-center">
            <div className="welcome-icon">🔐</div>
            <h2>로그인이 필요해요</h2>
            <p>AWS 리소스를 안전하게 관리하기 위해<br/>먼저 로그인해 주세요.</p>
            <Button
              variant="primary"
              size="lg"
              onClick={() => (window.location.href = '/login')}
            >
              로그인하기
            </Button>
          </Card.Content>
        </Card>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="page-background">
        <Card padding="lg">
          <Card.Content className="text-center">
            <LoadingSpinner size="md" />
            <span>사용자 정보를 불러오고 있어요</span>
          </Card.Content>
        </Card>
      </div>
    );
  }

  if (error) {
    return (
      <div className="page-background">
        <Card padding="lg">
          <Card.Content className="text-center">
            <div className="error-icon">😵</div>
            <h3>앗, 문제가 발생했어요</h3>
            <p>{error}</p>
            <div className="flex gap-3 justify-center">
              <Button
                variant="primary"
                onClick={fetchProfile}
                disabled={isRefreshing}
                loading={isRefreshing}
              >
                {isRefreshing ? '확인 중...' : '🔄 다시 시도'}
              </Button>
              <Button
                variant="secondary"
                onClick={() => window.location.reload()}
              >
                🔃 페이지 새로고침
              </Button>
            </div>
          </Card.Content>
        </Card>
      </div>
    );
  }

  const welcomeName = profile?.username || user?.username || '사용자';


  return (
    <div className="page-background page-container">
      {/* Header */}
      <Card padding="md" className="dashboard-header-card">
        <div className="gradient-primary" style={{position: 'absolute', top: 0, left: 0, right: 0, height: '4px'}}></div>
        <Card.Header>
          <div>
            <h1>안녕하세요, {welcomeName}님 👋</h1>
            <p className="dashboard-header-subtitle">AWS 리소스를 안전하게 관리하세요</p>
          </div>
          <Button
            variant="danger"
            size="md"
            onClick={logout}
            title="로그아웃"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
              <polyline points="16,17 21,12 16,7" />
              <line x1="21" y1="12" x2="9" y2="12" />
            </svg>
          </Button>
        </Card.Header>
      </Card>

      <div className="dashboard-content">
        {/* 프로필 카드 */}
        <Card hover padding="lg" className="profile-card">
          <div className="gradient-primary" style={{position: 'absolute', top: 0, left: 0, right: 0, height: '4px'}}></div>
          <Card.Header>
            <Card.Title>내 프로필</Card.Title>
            <div className="flex items-center gap-2">
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setShowPasswordModal(true)}
              >
                🔒
              </Button>
            </div>
          </Card.Header>
          
          <Card.Content>
            <div className="grid grid-cols-2 gap-4">
              <Card className="profile-info-card">
                <Card.Content className="text-center">
                  <div className="text-sm text-gray-500">이름</div>
                  <div className="font-semibold">{profile?.username || '-'}</div>
                </Card.Content>
              </Card>
              
              <Card className="profile-info-card">
                <Card.Content className="text-center">
                  <div className="text-sm text-gray-500">가입일</div>
                  <div className="font-semibold">{formatDate(profile?.createdAt)}</div>
                </Card.Content>
              </Card>
              
              {profile?.accessLevel && (
                <Card className="profile-info-card">
                  <Card.Content className="text-center">
                    <div className="text-sm text-gray-500">권한</div>
                    <Badge
                      variant={profile.accessLevel === 'full' ? 'success' : 
                              profile.accessLevel === 'limited' ? 'warning' : 'error'}
                      size="sm"
                    >
                      {profile.accessLevel === 'full' ? '전체' : 
                       profile.accessLevel === 'limited' ? '제한' : '없음'}
                    </Badge>
                  </Card.Content>
                </Card>
              )}
            </div>
            

          </Card.Content>
        </Card>
      </div>

      {/* Password Change Modal */}
      <Modal isOpen={showPasswordModal} onClose={handleClosePasswordModal}>
        <Card padding="lg">
              <Card.Header>
                <Card.Title>🔒 비밀번호 변경</Card.Title>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleClosePasswordModal}
                  aria-label="모달 닫기"
                >
                  ✕
                </Button>
              </Card.Header>

              <Card.Content>
                <form onSubmit={handlePasswordSubmit} className="flex flex-col gap-5">
                  <Input
                    type="password"
                    label="현재 비밀번호"
                    name="currentPassword"
                    value={passwordForm.currentPassword}
                    onChange={handlePasswordChange}
                    placeholder="현재 비밀번호를 입력하세요"
                    required
                  />

                  <Input
                    type="password"
                    label="새 비밀번호"
                    name="newPassword"
                    value={passwordForm.newPassword}
                    onChange={handlePasswordChange}
                    placeholder="새 비밀번호를 입력하세요 (최소 8자)"
                    required
                    minLength="8"
                  />

                  <Input
                    type="password"
                    label="새 비밀번호 확인"
                    name="confirmPassword"
                    value={passwordForm.confirmPassword}
                    onChange={handlePasswordChange}
                    placeholder="새 비밀번호를 다시 입력하세요"
                    required
                  />



                  <Card.Actions align="right" className="form-actions">
                    <Button
                      type="button"
                      variant="secondary"
                      onClick={handleClosePasswordModal}
                      disabled={isChangingPassword}
                    >
                      취소
                    </Button>
                    <Button
                      type="submit"
                      variant="primary"
                      disabled={isChangingPassword}
                      loading={isChangingPassword}
                    >
                      {isChangingPassword ? '변경 중...' : '비밀번호 변경'}
                    </Button>
                  </Card.Actions>
                </form>
              </Card.Content>
        </Card>
      </Modal>

    </div>
  );
};

export default UserDashboard;